import { firestore } from 'firebase';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);
dayjs.locale('ja');

type dateProps = Date | string | undefined | firestore.Timestamp | null;

// 指定されたms待機
export const sleep = (msec: number) => new Promise((resolve) => setTimeout(resolve, msec));

/**
 * 値が存在するか否かを全般的にチェック
 */
export const isNullOrUndefinedOrEmptyOrZero = (value: any) =>
  value === null ||
  value === undefined ||
  value === '' ||
  value === 0 ||
  ((typeof value === 'object' && Object.keys(value).length === 0) as boolean);

/**
 * 日付のフォーマット
 */
export const formatDate = (value: dateProps, format: string = 'YYYY年MM月DD日 HH:mm:ss') => {
  let date;
  if (value instanceof firestore.Timestamp) {
    date = value.toDate();
  } else if (typeof value === undefined || value === null) {
    date = new Date();
  } else {
    date = value;
  }
  return dayjs(date).format(format);
};

/**
 * 日付をdayjs経由でobjectに戻す。FirestoreのTimestampと通常のDateオブジェクトと区別なく使用可能
 */
export const formatDateToDate = (value: dateProps) => {
  let date;
  if (value instanceof firestore.Timestamp) {
    date = value.toDate();
  } else if (typeof value === undefined || value === null) {
    date = new Date();
  } else {
    date = value;
  }
  return dayjs(date).toDate();
};

/**
 * 日付のフォーマット（検索クエリ用）
 */
export const formatDateAsQuery = (date?: dateProps) =>
  dayjs(formatDateToDate(date)).format('YYYY-MM-DD');

/**
 * 2つの日付が同じ日かチェック
 */
export const isSameDay = (a: dateProps, b: dateProps) => {
  const x = dayjs(formatDateToDate(a)).format('YYYY-MM-DD');
  return dayjs(formatDateToDate(b)).isSame(x, 'day');
};

/**
 * 時刻をn時分秒廟単位でまるめる
 */
export const roundDate = (date: Date, num: number, target: 'hour' | 'minute' | 'second') => {
  const day = dayjs(date);
  const value = Math.ceil(day[target]() / num) * num;
  let result: Date;
  switch (target) {
    case 'hour':
      result = day.hour(value).minute(0).second(0).toDate();
      break;
    case 'minute':
      result = day.minute(value).second(0).toDate();
      break;
    case 'second':
      result = day.second(value).toDate();
      break;
    default:
      result = date;
      break;
  }
  return result;
};

/**
 * n日前かを日本語で取得
 */
export const daysAgo = (date?: Date | string | undefined) => dayjs().to(dayjs(date || new Date()));

/**
 * 指定された日付の年月日だけコピーする。
 * フロントエンドで new Date すると過去日付が入ってきた事例があったため...
 * Firebaseの最新のサーバータイムスタンプ（created_at, updated_at）を転写するために作った。
 */
export const copyDateOnlyYYYYMMDD = (target: Date, ymd: Date) => {
  const now = new Date(ymd);
  const date = new Date(target);
  date.setFullYear(now.getFullYear());
  date.setMonth(now.getMonth());
  date.setDate(now.getDate());
  return date;
};

/**
 * 日付の時刻だけコピーする。ほぼテンプレート機能用。
 */
export const copyTimeOnlyHHmmss = (hhmmss: Date) => {
  const time = new Date(hhmmss);
  const now = new Date();
  now.setHours(time.getHours());
  now.setMinutes(time.getMinutes());
  now.setSeconds(time.getSeconds());
  return now;
};

/**
 * 指定されたキーを除外した配列を返す
 */
export const excludeByKeys = (entityKeys: string[], targets: string[]) =>
  entityKeys.filter((key) => !targets.includes(key));

/**
 * トースターを表示
 */

export const toaster = (
  type: 'dark' | 'success' | 'info' | 'warning' | 'error',
  text: string,
  autoClose?: number,
) =>
  toast[type](text, {
    position: 'bottom-center',
    autoClose: autoClose || 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    progress: undefined,
  });
